/* @flow */
import {
  type AgeGroup,
  type Customer,
  type Gender,
  type Order,
  type SushiKind,
  ageGroup,
  gender,
  order,
  sushi,
} from './types';
import { Random } from '../../javascripts/utils/Random';
import { ageGroupGen, customerKindGen, genderGen } from './common';

// constants

const firstOrders = {
  [gender.MALE]: [
    { p: 0.8, order: order.forKind(1) },
    { p: 0.2, order: order.forKind(3) },
  ],
  [gender.FEMALE]: [
    { p: 0.8, order: order.forKind(4) },
    { p: 0.2, order: order.forKind(2) },
  ],
};

const secondOrders = {
  [ageGroup.CHILD]: {
    [gender.MALE]: [
      { p: 0.2, order: order.forKind(2) },
      { p: 0.6, order: order.forKind(3) },
      { p: 0.2, order: order.forKind(4) },
    ],
    [gender.FEMALE]: [
      { p: 0.2, order: order.forKind(1) },
      { p: 0.6, order: order.forKind(2) },
      { p: 0.2, order: order.forKind(3) },
    ],
  },
  [ageGroup.ADULT]: {
    [gender.MALE]: [
      { p: 0.6, order: order.forKind(1) },
      { p: 0.2, order: order.forKind(2) },
      { p: 0.2, order: order.forKind(4) },
    ],
    [gender.FEMALE]: [
      { p: 0.2, order: order.forKind(1) },
      { p: 0.5, order: order.forKind(2) },
      { p: 0.3, order: order.forKind(4) },
    ],
  },
  [ageGroup.MIDDLE_AGE]: {
    [gender.MALE]: [
      { p: 0.3, order: order.forKind(1) },
      { p: 0.5, order: order.forKind(3) },
      { p: 0.2, order: order.forKind(4) },
    ],
    [gender.FEMALE]: [
      { p: 0.2, order: order.forKind(1) },
      { p: 0.2, order: order.forKind(3) },
      { p: 0.6, order: order.forKind(4) },
    ],
  },
};

const thirdOrders = {
  [ageGroup.CHILD]: {
    [gender.MALE]: [
      { p: 0.1, order: order.forKind(1) },
      { p: 0.4, order: order.forKind(2) },
      { p: 0.4, order: order.forKind(3) },
      { p: 0.1, order: order.forKind(4) },
    ],
    [gender.FEMALE]: [
      { p: 0.1, order: order.forKind(1) },
      { p: 0.4, order: order.forKind(2) },
      { p: 0.4, order: order.forKind(3) },
      { p: 0.1, order: order.forKind(4) },
    ],
  },
  [ageGroup.ADULT]: {
    [gender.MALE]: [
      { p: 0.4, order: order.forKind(1) },
      { p: 0.4, order: order.forKind(2) },
      { p: 0.1, order: order.forKind(3) },
      { p: 0.1, order: order.forKind(4) },
    ],
    [gender.FEMALE]: [
      { p: 0.4, order: order.forKind(1) },
      { p: 0.2, order: order.forKind(2) },
      { p: 0.4, order: order.forKind(4) },
    ],
  },
  [ageGroup.MIDDLE_AGE]: {
    [gender.MALE]: [
      { p: 0.4, order: order.forKind(1) },
      { p: 0.2, order: order.forKind(3) },
      { p: 0.4, order: order.forKind(4) },
    ],
    [gender.FEMALE]: [
      { p: 0.1, order: order.forKind(1) },
      { p: 0.1, order: order.forKind(2) },
      { p: 0.4, order: order.forKind(3) },
      { p: 0.4, order: order.forKind(4) },
    ],
  },
};

const forthOrders: { [SushiKind]: SushiKind } = {
  [1]: 3,
  [2]: 4,
  [3]: 1,
  [4]: 2,
};

const fifthOrders = {
  [ageGroup.CHILD]: {
    [gender.MALE]: order.forSushi(sushi.new(3, 3)),
    [gender.FEMALE]: order.forSushi(sushi.new(2, 1)),
  },
  [ageGroup.ADULT]: {
    [gender.MALE]: order.forSushi(sushi.new(1, 3)),
    [gender.FEMALE]: order.forSushi(sushi.new(1, 1)),
  },
  [ageGroup.MIDDLE_AGE]: {
    [gender.MALE]: order.forSushi(sushi.new(4, 3)),
    [gender.FEMALE]: order.forSushi(sushi.new(4, 2)),
  },
};

const makeOrdersGen = (gender: Gender, ageGroup: AgeGroup) => {
  const first = Random.ofProbabilities(...firstOrders[gender].map(({ p, order }) => [order, p]));
  const second = Random.ofProbabilities(...secondOrders[ageGroup][gender].map(({ p, order }) => [order, p]));
  const third = Random.ofProbabilities(...thirdOrders[ageGroup][gender].map(({ p, order }) => [order, p]));
  const forth: Random<Order> = Random.const((toMatch, eatens) => {
    if (!eatens || !eatens.length) return false;
    return toMatch.subkind === eatens[0].subkind && toMatch.kind === forthOrders[eatens[0].kind];
  });
  const fifth = Random.const(fifthOrders[ageGroup][gender]);

  return Random.traverse([first, second, third, forth, fifth], x => x);
};

export const AGE_BASE = {
  [ageGroup.CHILD]: 7,
  [ageGroup.ADULT]: 30,
  [ageGroup.MIDDLE_AGE]: 70,
};

export const AGE_RANGE = {
  [ageGroup.CHILD]: 8,
  [ageGroup.ADULT]: 9,
  [ageGroup.MIDDLE_AGE]: 9,
};

export const customerGen: Random<Customer> = Random.map4(
  genderGen,
  ageGroupGen.map(ag => [ag, AGE_RANGE[ag]]),
  customerKindGen,
  Random.int(0, 10),
  (gender, [ageGrp, range], kind) => {
    let age = range + AGE_BASE[ageGrp];
    return {
      gender,
      ageGroup: ageGrp,
      kind,
      age,
    };
  }
).bind(partial => {
  return makeOrdersGen(partial.gender, partial.ageGroup).map(orders => ({ ...partial, orders }));
});
